import React, { useMemo, useState } from 'react';
import { PRIMARY_RUBRIC_DIMENSIONS } from '../config/rubricDimensions';
import { DIMENSION_GUIDANCE } from '../config/dimensionGuidance';
import type { ClientProfile as DimensionRow } from './DimensionProfileChart';
import FilterSelect from './FilterSelect';

/**
 * Red flags about how clients are SELLING, not how they are paying.
 *
 * The existing risk flags (`atRisk.js`) are entirely commercial — past due,
 * cancel scheduled, trial expiring, inactive seats. They answer "who might
 * churn on paper". They cannot answer "who is getting no value", which is the
 * question that predicts the churn before the billing system knows about it.
 *
 * A client whose reps never qualify budget is not a billing problem yet. It is
 * the reason they will become one.
 */

export type CoachingFlagSeverity = 'critical' | 'warning';

export interface CoachingFlag {
  tenantId: string;
  clientName: string;
  severity: CoachingFlagSeverity;
  /** The short headline, e.g. "Pain Discovery at 38%". */
  title: string;
  /** The BEHAVIOUR behind the score, in the rubric's own failure language. */
  problem: string;
  /** The one move to coach. */
  fix: string;
  /** Scored calls this average is drawn from — how much to trust it. */
  sessions: number;
  /** Tier-1 dimensions are the "at any cost" set. */
  isTierOne: boolean;
}

interface CoachingFlagsProps {
  rows: DimensionRow[];
  /** Ignore clients with fewer scored calls than this — noise, not signal. */
  minSessions?: number;
  maxFlags?: number;
}

/** The rubric's own soft-call line: below this the deal is alive on hope. */
const SOFT_LINE = 60;
/** Below this the rubric says the deal is at real risk. */
const CRITICAL_LINE = 45;
/** Fewer scored calls than this and an average is an anecdote. */
const DEFAULT_MIN_SESSIONS = 3;

/**
 * Tier-1 dimensions — the "at any cost" set, identical to the four the live
 * checklist prioritises and the weight editor protects. Weakness here is
 * reported a step more severely than elsewhere, because the rubric treats these
 * as the moves a discovery call cannot do without.
 */
const TIER_ONE = new Set([
  'up_front_contract_posture',
  'pain_discovery_levels',
  'pain_with_price_tag',
  'up_front_contract_close'
]);

export function buildCoachingFlags(
  rows: DimensionRow[],
  minSessions = DEFAULT_MIN_SESSIONS
): CoachingFlag[] {
  const flags: CoachingFlag[] = [];

  rows.forEach((row) => {
    if ((row.sessions ?? 0) < minSessions) return;

    // Only the five reported dimensions raise flags. A list topped by "Coach's
    // Discipline at 30%" buries the tier-1 misses underneath it, which is the
    // opposite of what a red-flag list is for.
    PRIMARY_RUBRIC_DIMENSIONS.forEach((dimension) => {
      const value = row.dimensions?.[dimension.key];
      if (typeof value !== 'number' || value >= SOFT_LINE) return;

      const isTierOne = TIER_ONE.has(dimension.key);
      const severity: CoachingFlagSeverity =
        value < CRITICAL_LINE || (isTierOne && value < SOFT_LINE - 5)
          ? 'critical'
          : 'warning';

      flags.push({
        tenantId: row.tenantId,
        clientName: row.name,
        severity,
        title: `${dimension.label} at ${Math.round(value)}%`,
        problem: DIMENSION_GUIDANCE[dimension.key].problem,
        fix: DIMENSION_GUIDANCE[dimension.key].fix,
        sessions: row.sessions ?? 0,
        isTierOne
      });
    });
  });

  // Worst first, and criticals ahead of warnings at equal depth.
  return flags.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'critical' ? -1 : 1;
    return a.title.localeCompare(b.title);
  });
}

/** Sentinel for the "no filter" option. Not a tenantId, so it cannot collide. */
const ALL_CLIENTS = '__all__';

const CoachingFlags: React.FC<CoachingFlagsProps> = ({
  rows,
  minSessions = DEFAULT_MIN_SESSIONS,
  maxFlags = 12
}) => {
  const allFlags = useMemo(
    () => buildCoachingFlags(rows, minSessions),
    [rows, minSessions]
  );

  // Default to the client carrying the most CRITICAL flags rather than to "all".
  // The unfiltered list is one long run-on of every client's problems, which is
  // what made this section unreadable; landing on the worst client makes the
  // first thing you see the thing most worth acting on.
  const defaultClient = useMemo(() => {
    const criticals = new Map<string, number>();
    allFlags.forEach((f) => {
      if (f.severity !== 'critical') return;
      criticals.set(f.tenantId, (criticals.get(f.tenantId) ?? 0) + 1);
    });
    const worst = [...criticals.entries()].sort((a, b) => b[1] - a[1])[0];
    return worst ? worst[0] : allFlags[0]?.tenantId ?? ALL_CLIENTS;
  }, [allFlags]);

  const [selected, setSelected] = useState<string | null>(null);
  const active = selected ?? defaultClient;

  // Only clients that actually have flags — offering a client with nothing to
  // show is a dead end the reader has to back out of.
  const options = useMemo(() => {
    const byId = new Map<string, string>();
    allFlags.forEach((f) => byId.set(f.tenantId, f.clientName));
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [allFlags]);

  const flags = useMemo(
    () => (active === ALL_CLIENTS ? allFlags : allFlags.filter((f) => f.tenantId === active)),
    [allFlags, active]
  );

  if (!rows.length) {
    return <div className="empty">No scored calls in this range.</div>;
  }

  if (!allFlags.length) {
    return (
      <div className="empty">
        No client is below the {SOFT_LINE}% soft-call line on any dimension.
      </div>
    );
  }

  const shown = flags.slice(0, maxFlags);

  return (
    <div className="rank coaching-flags">
      <FilterSelect
        id="flag-client"
        label="Client"
        value={active}
        onChange={setSelected}
        options={[
          ...options.map(([id, name]) => ({ id, label: name })),
          { id: ALL_CLIENTS, label: `All clients (${allFlags.length})` }
        ]}
        hint={`${flags.length} ${flags.length === 1 ? 'flag' : 'flags'} below the ${SOFT_LINE}% line`}
      />

      {shown.map((flag, index) => (
        <div className="rank-row" key={`${flag.tenantId}-${flag.title}-${index}`}>
          {/* Icon AND word carry the severity — never colour on its own. */}
          <span
            className={`flag-badge ${flag.severity}`}
            title={
              flag.severity === 'critical'
                ? 'Deal-at-risk territory on the rubric scale'
                : 'Below the soft-call line'
            }
          >
            {flag.severity === 'critical' ? '■ Critical' : '▲ Warning'}
          </span>
          <div className="grow">
            <div className="flag-title">
              {flag.title}
              {flag.isTierOne && <span className="tier-pill">tier 1</span>}
              <span className="flag-meta">
                {flag.clientName} · {flag.sessions} scored{' '}
                {flag.sessions === 1 ? 'call' : 'calls'}
              </span>
            </div>
            <div className="flag-problem">{flag.problem}</div>
            <div className="flag-fix">
              <span className="flag-fix-tag">Coach</span> {flag.fix}
            </div>
          </div>
        </div>
      ))}
      {flags.length > shown.length && (
        <div className="cl-sub" style={{ padding: '6px 0' }}>
          +{flags.length - shown.length} more below the line
        </div>
      )}
    </div>
  );
};

export default CoachingFlags;
